import type { ListItemVariant } from '@sunwebgroup/uds-ui/list-item'
import type { RadioButtonGroupProps } from '@sunwebgroup/uds-ui/radio-button'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { RadioButtonGroup } from '@sunwebgroup/uds-ui/radio-button'
import React, { createContext, use, useMemo, useRef } from 'react'

const DOUBLE_CLICK_GUARD_MS = 400

interface RadioGroupContextValue {
  onValueChange: (value: string) => void
  value: string | null | undefined
}

const RadioGroupContext = createContext<RadioGroupContextValue | null>(null)

export function SelectableRadioButtonGroup({ onValueChange, value, children, ...rest }: RadioButtonGroupProps) {
  const contextValue = useMemo(
    () => ({ onValueChange: onValueChange ?? (() => {}), value }),
    [onValueChange, value],
  )

  return (
    <RadioGroupContext value={contextValue}>
      <RadioButtonGroup value={value} onValueChange={onValueChange} {...rest}>
        {children}
      </RadioButtonGroup>
    </RadioGroupContext>
  )
}

interface SelectableListItemProps {
  value: string
  children: React.ReactNode
  className?: string
  variant?: ListItemVariant
  disabled?: boolean
}

export function SelectableListItem({ value, children, className, variant = 'outlined', disabled = false }: SelectableListItemProps) {
  const context = use(RadioGroupContext)
  const lastClickAtRef = useRef(0)

  const handleClick = () => {
    if (disabled)
      return

    const now = Date.now()

    if (now - lastClickAtRef.current < DOUBLE_CLICK_GUARD_MS)
      return

    lastClickAtRef.current = now
    context?.onValueChange(value)
  }

  return (
    <ListItem
      variant={variant}
      className={className}
      hasHover={!disabled}
      onClick={handleClick}
    >
      {children}
    </ListItem>
  )
}
